import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CircuitBoard,
  MessageSquare,
  BarChart2,
  Users,
  Code2,
  Bug,
  Settings
} from 'lucide-react';
import NavbarContext from '../../context/NavbarContext/NavbarContext';
import NavItem from './NavItem/NavItem';
import JapetoLogo from '../../assets/images/japeto-logo.svg';
import useAuthRoles from '../../hooks/useAuthRoles';
import OrgChatbotSwitcher from './OrgChatbotSwitcher/OrgChatbotSwitcher';
import Button from '../Button/Button';

function Navbar(): React.ReactElement {
  const { navbarOpen, setNavbarOpen } = React.useContext(NavbarContext);
  const { userCanWrite, userIsAdmin, userIsJapetoAdmin } = useAuthRoles();
  const isCollapsed = !navbarOpen;

  const toggleDrawer = (): void => {
    setNavbarOpen(!navbarOpen);
  };

  return (
    <div
      className={`
        flex flex-col h-full bg-[#057D99] text-white shadow-md transition-[width] duration-300 ease-in-out relative
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}
    >
      <div
        className={`
          flex flex-col items-center border-b border-white/20 transition-all duration-300 ease-in-out
          ${isCollapsed ? 'px-2 pt-1 pb-0' : 'px-4 pt-4 pb-3'}
        `}
      >
        <Link
          to='/home'
          className={`transition-all duration-300 ease-in-out ${isCollapsed ? 'mb-0 -mt-2' : 'mb-2'}`}
        >
          <div
            className={`w-32 flex items-center justify-center transition-all duration-300 ease-in-out ${isCollapsed ? 'min-h-[12px]' : 'min-h-[79px]'}`}
          >
            <img
              src={JapetoLogo}
              alt='Japeto Logo'
              className={`
                w-32 h-auto transition-transform duration-300 ease-in-out origin-center
                ${isCollapsed ? 'scale-[0.35]' : 'scale-100'}
              `}
            />
          </div>
        </Link>
        <OrgChatbotSwitcher collapsed={isCollapsed} />
      </div>

      <nav className='flex-grow mt-2 relative flex flex-col'>
        {!isCollapsed && (
          <div className='px-4 pt-4 pb-1'>
            <h3 className='text-xs font-semibold uppercase tracking-wider opacity-70'>
              Manage Chatbot
            </h3>
          </div>
        )}

        <NavItem
          href='/chats'
          icon={<MessageSquare size={20} />}
          label='Conversations'
          isCollapsed={isCollapsed}
        />
        <NavItem
          href='/analytics'
          icon={<BarChart2 size={20} />}
          label='Analytics'
          isCollapsed={isCollapsed}
        />
        {userCanWrite() && (
          <NavItem
            href='/training'
            icon={<CircuitBoard size={20} />}
            label='Training'
            isCollapsed={isCollapsed}
          />
        )}
        {userCanWrite() && (
          <NavItem
            href='/settings'
            icon={<Settings size={20} />}
            label='Settings'
            isCollapsed={isCollapsed}
          />
        )}

        {isCollapsed ? (
          <div className='mx-auto my-2 h-px w-8 bg-white/20' />
        ) : (
          <div className='px-4 pt-4 pb-1'>
            <h3 className='text-xs font-semibold uppercase tracking-wider opacity-70'>
              Advanced
            </h3>
          </div>
        )}

        {userIsAdmin() && (
          <NavItem
            href='/users'
            icon={<Users size={20} />}
            label='Manage users'
            isCollapsed={isCollapsed}
          />
        )}
        {userIsAdmin() && (
          <NavItem
            href='/org-settings'
            icon={<Settings size={20} />}
            label='Organisation'
            isCollapsed={isCollapsed}
          />
        )}
        {userIsJapetoAdmin() && (
          <NavItem
            href='/issues'
            icon={<Bug size={20} />}
            label='Issues'
            isCollapsed={isCollapsed}
          />
        )}
        <NavItem
          href='/embed-instructions'
          icon={<Code2 size={20} />}
          label='Embedding'
          isCollapsed={isCollapsed}
        />

        <Button
          variant='ghost'
          size='icon'
          onClick={toggleDrawer}
          className='absolute bottom-4 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full
              sm:bottom-auto sm:top-[45%] sm:left-auto sm:right-0 sm:translate-x-0 sm:-translate-y-1/2 sm:w-6 sm:h-12 sm:rounded-r-none
              text-white hover:text-gray-600 bg-[#057D99] hover:bg-white transition-all'
          aria-label='Toggle navigation drawer'
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </Button>
      </nav>
    </div>
  );
}

export default Navbar;
